import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Mail, MapPin, Send, Wallet, Shield, Eye, Scissors, DollarSign,
  ExternalLink, PlusCircle, MinusCircle, ClipboardList, RefreshCw, XCircle,
  CheckCircle2, Copy, User,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  adminGetClipperProfile, adminAdjustClipperBalance, apiErrMsg,
} from '../../lib/api';

function fmt$(n) { return `$${Number(n || 0).toFixed(2)}`; }
function fmtN(n) { return Number(n || 0).toLocaleString(); }
function shortDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

function bandStyle(band) {
  switch (band) {
    case 'clean':
      return { color: 'text-[hsl(140_65%_70%)]', bg: 'bg-[rgba(46,204,113,0.10)]', border: 'border-[rgba(46,204,113,0.55)]', label: 'CLEAN' };
    case 'watch':
      return { color: 'text-[hsl(43_92%_72%)]', bg: 'bg-[rgba(245,185,66,0.10)]', border: 'border-[rgba(245,185,66,0.55)]', label: 'WATCH' };
    case 'risky':
      return { color: 'text-[hsl(20_85%_70%)]', bg: 'bg-[rgba(230,120,50,0.12)]', border: 'border-[rgba(230,120,50,0.55)]', label: 'RISKY' };
    case 'high_risk':
      return { color: 'text-[hsl(0_72%_70%)]', bg: 'bg-[rgba(225,29,46,0.12)]', border: 'border-[rgba(225,29,46,0.55)]', label: 'HIGH RISK' };
    default:
      return { color: 'text-muted-foreground', bg: 'bg-white/[0.02]', border: 'border-white/10', label: (band || 'NEW').toUpperCase() };
  }
}

export default function AdminClipperProfileDialog({ open, onOpenChange, clipperId, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!clipperId) return;
    setLoading(true);
    try {
      const r = await adminGetClipperProfile(clipperId);
      setData(r);
    } catch (e) {
      toast.error(apiErrMsg(e, 'Failed to load profile'));
    } finally { setLoading(false); }
  }, [clipperId]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  const clipper = data?.clipper;
  const trust = data?.trust;
  const clips = data?.clips || [];
  const withdrawals = data?.withdrawals || [];
  const adjustments = data?.adjustments || [];
  const summary = data?.summary || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl bg-[hsl(220_10%_6%)] border-white/10 max-h-[92vh] flex flex-col"
        data-testid="clipper-profile-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <User className="h-5 w-5 text-[hsl(43_92%_56%)]" /> Clipper profile
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Inspect clip history, SMM trust signals, balances, and adjust funds manually.
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading profile…
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-2">
            <div className="space-y-4">
              {/* Identity + trust */}
              <IdentityCard clipper={clipper} trust={trust} onCopy={(v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); }} />

              {/* Balance summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <MiniStat Icon={Eye} label="Total views" value={fmtN(clipper.total_views)} />
                <MiniStat Icon={DollarSign} label="Lifetime earned" value={fmt$(clipper.total_earned_usd)} />
                <MiniStat Icon={Wallet} label="Pending (withdrawable)" value={fmt$(clipper.pending_balance_usd)} gold />
                <MiniStat Icon={Shield} label="Held (SMM review)" value={fmt$(clipper.held_balance_usd)} danger={clipper.held_balance_usd > 0} />
                <MiniStat Icon={CheckCircle2} label="Paid out" value={fmt$(clipper.total_paid_out_usd)} />
                <MiniStat Icon={Scissors} label="Active clips" value={summary.active_clips || 0} />
                <MiniStat Icon={XCircle} label="Blocked / removed" value={summary.blocked_clips || 0} danger={(summary.blocked_clips || 0) > 0} />
                <MiniStat Icon={ClipboardList} label="Total withdrawn" value={fmt$(summary.total_withdrawn_usd || 0)} />
              </div>

              {/* Adjust balance */}
              <AdjustBalanceCard
                clipper={clipper}
                onAdjusted={async () => {
                  await load();
                  if (onChanged) onChanged();
                }}
              />

              {/* Tabs: clips / withdrawals / audit */}
              <Tabs defaultValue="clips">
                <TabsList className="bg-white/[0.02] border border-white/10 p-1 gap-1">
                  <TabsTrigger value="clips" data-testid="profile-tab-clips"
                    className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                    Clips ({clips.length})
                  </TabsTrigger>
                  <TabsTrigger value="withdrawals" data-testid="profile-tab-withdrawals"
                    className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                    Withdrawals ({withdrawals.length})
                  </TabsTrigger>
                  <TabsTrigger value="audit" data-testid="profile-tab-audit"
                    className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
                    Audit log ({adjustments.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="clips" className="mt-3">
                  <ClipsList clips={clips} />
                </TabsContent>
                <TabsContent value="withdrawals" className="mt-3">
                  <WithdrawalsList withdrawals={withdrawals} />
                </TabsContent>
                <TabsContent value="audit" className="mt-3">
                  <AuditList adjustments={adjustments} />
                </TabsContent>
              </Tabs>

              <div className="flex justify-end pb-2">
                <Button size="sm" variant="outline" className="h-8 border-white/10 bg-white/[0.02] gap-1.5" onClick={load}
                  data-testid="profile-refresh">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </Button>
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IdentityCard({ clipper, trust, onCopy }) {
  const bs = bandStyle(trust?.band);
  const handles = clipper?.social_handles || {};
  const platformHandles = [
    { plat: 'tiktok', list: handles.tiktok || [], color: 'text-white' },
    { plat: 'instagram', list: handles.instagram || [], color: 'text-[hsl(320_70%_78%)]' },
    { plat: 'facebook', list: handles.facebook || [], color: 'text-[hsl(212_85%_78%)]' },
  ];
  return (
    <Card className="card-surface" data-testid="profile-identity">
      <CardContent className="p-4 grid gap-4 md:grid-cols-[1fr,auto] items-start">
        <div className="min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg truncate">{clipper?.name || '(unnamed)'}</div>
            {clipper?.blocked && (
              <Badge variant="outline" className="border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] text-[10px] uppercase tracking-[0.18em]">
                Blocked
              </Badge>
            )}
          </div>
          <div className="grid gap-1 text-xs text-muted-foreground">
            <button type="button" onClick={() => onCopy(clipper?.email)} className="inline-flex items-center gap-1.5 hover:text-white text-left" data-testid="copy-email">
              <Mail className="h-3.5 w-3.5" /> {clipper?.email || '—'} <Copy className="h-3 w-3 opacity-60" />
            </button>
            {clipper?.country && (
              <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {clipper.country}</div>
            )}
            {clipper?.telegram_handle && (
              <a href={`https://t.me/${(clipper.telegram_handle || '').replace(/^@/, '')}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[hsl(212_85%_78%)] hover:underline">
                <Send className="h-3.5 w-3.5" /> {clipper.telegram_handle} <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {clipper?.usdt_bep20_address && (
              <button type="button" onClick={() => onCopy(clipper.usdt_bep20_address)}
                className="inline-flex items-center gap-1.5 hover:text-white font-mono text-[10px] break-all text-left" data-testid="copy-usdt">
                <Wallet className="h-3.5 w-3.5 shrink-0" /> {clipper.usdt_bep20_address} <Copy className="h-3 w-3 opacity-60" />
              </button>
            )}
          </div>
          <div className="grid gap-1 pt-1">
            {platformHandles.map(({ plat, list, color }) => list.length > 0 && (
              <div key={plat} className="text-[11px]">
                <span className="text-muted-foreground uppercase tracking-[0.18em]">{plat}:</span>{' '}
                <span className={`font-mono ${color}`}>{list.map((h) => `@${h}`).join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`rounded-lg border ${bs.border} ${bs.bg} p-3 min-w-[180px]`}>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
            <Shield className={`h-3.5 w-3.5 ${bs.color}`} /> SMM Trust Score
          </div>
          <div className={`font-display text-3xl tabular-nums mt-1 ${bs.color}`}>
            {trust?.score ?? '—'}
            <span className="text-sm text-muted-foreground"> / 100</span>
          </div>
          <div className={`text-[10px] uppercase tracking-[0.22em] mt-1 ${bs.color}`}>{bs.label}</div>
          {trust?.signals && trust.signals.total_clips > 0 && (
            <div className="text-[10px] text-muted-foreground mt-2 space-y-0.5">
              <div>{trust.signals.total_clips} total clips</div>
              {trust.signals.hard_flagged > 0 && <div>🔴 {trust.signals.hard_flagged} SMM-flagged</div>}
              {trust.signals.confirmed_fraud > 0 && <div>❌ {trust.signals.confirmed_fraud} confirmed fraud</div>}
              {trust.signals.confirmed_organic > 0 && <div>✓ {trust.signals.confirmed_organic} confirmed organic</div>}
              {trust.signals.blocked_or_removed > 0 && <div>⊘ {trust.signals.blocked_or_removed} blocked/removed</div>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ Icon, label, value, gold, danger }) {
  return (
    <div className={
      'rounded-md border p-3 ' +
      (gold ? 'border-[rgba(245,185,66,0.4)] bg-[rgba(245,185,66,0.04)]' :
       danger ? 'border-[rgba(225,29,46,0.4)] bg-[rgba(225,29,46,0.04)]' :
       'border-white/10 bg-white/[0.02]')
    }>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
        <Icon className={'h-3 w-3 ' + (gold ? 'text-[hsl(43_92%_56%)]' : danger ? 'text-[hsl(0_72%_70%)]' : '')} /> {label}
      </div>
      <div className={
        'font-display text-lg mt-0.5 tabular-nums ' +
        (gold ? 'text-[hsl(43_92%_56%)]' : danger ? 'text-[hsl(0_72%_70%)]' : 'text-white')
      }>{value}</div>
    </div>
  );
}

function AdjustBalanceCard({ clipper, onAdjusted }) {
  const [amount, setAmount] = useState('');
  const [bucket, setBucket] = useState('pending');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(sign) {
    const raw = Number(amount);
    if (!Number.isFinite(raw) || raw === 0) {
      toast.error('Enter a non-zero amount');
      return;
    }
    const signed = sign === 'add' ? Math.abs(raw) : -Math.abs(raw);
    setSaving(true);
    try {
      await adminAdjustClipperBalance(clipper.id, { amount_usd: signed, bucket, note });
      toast.success(`${sign === 'add' ? 'Added' : 'Deducted'} ${fmt$(Math.abs(signed))} to ${bucket}`);
      setAmount('');
      setNote('');
      if (onAdjusted) onAdjusted();
    } catch (e) { toast.error(apiErrMsg(e, 'Adjustment failed')); }
    finally { setSaving(false); }
  }

  return (
    <Card className="card-surface" data-testid="adjust-balance-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[hsl(43_92%_56%)]" />
          <div className="font-display text-sm">Manual balance adjustment</div>
          <div className="text-[10px] text-muted-foreground">(logged to audit trail)</div>
        </div>
        <div className="grid gap-2 sm:grid-cols-[1fr,1fr,2fr]">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Amount (USD)</Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00" className="bg-white/[0.02] border-white/10 font-mono tabular-nums" data-testid="adjust-amount" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Bucket</Label>
            <select value={bucket} onChange={(e) => setBucket(e.target.value)}
              className="h-9 w-full bg-white/[0.02] border border-white/10 rounded-md px-2 text-sm" data-testid="adjust-bucket">
              <option value="pending">Pending (withdrawable)</option>
              <option value="held">Held (SMM review)</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Reason / note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. refund of wrongly blocked clip #abcd1234"
              className="bg-white/[0.02] border-white/10 text-xs" data-testid="adjust-note" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={saving} onClick={() => submit('add')}
            className="cta-sheen border-0 gap-1.5 h-8" data-testid="adjust-add-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5" />} Add funds
          </Button>
          <Button size="sm" variant="outline" disabled={saving} onClick={() => submit('deduct')}
            className="h-8 border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] hover:bg-[rgba(225,29,46,0.06)] gap-1.5"
            data-testid="adjust-deduct-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MinusCircle className="h-3.5 w-3.5" />} Deduct
          </Button>
          <div className="text-[10px] text-muted-foreground">
            Current: pending {fmt$(clipper?.pending_balance_usd)} · held {fmt$(clipper?.held_balance_usd)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ClipsList({ clips }) {
  if (clips.length === 0) {
    return <div className="text-sm text-muted-foreground text-center p-6 border border-white/10 rounded-md bg-white/[0.02]">No clips submitted yet</div>;
  }
  return (
    <div className="grid gap-2" data-testid="profile-clips-list">
      {clips.map((c) => {
        const statusColor = c.status === 'active' ? 'border-white/10 text-muted-foreground'
          : 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]';
        const flag = c.engagement_flag || 'ok';
        return (
          <div key={c.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap"
            data-testid={`profile-clip-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/10">{c.platform}</Badge>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.18em] ${statusColor}`}>{c.status}</Badge>
                {flag === 'hard' && <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]">🔴 SMM</Badge>}
                {flag === 'soft' && <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-[rgba(245,185,66,0.55)] text-[hsl(43_92%_72%)]">🟡 soft</Badge>}
                <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(43_92%_72%)] hover:underline truncate max-w-[320px] inline-flex items-center gap-1">
                  {c.url} <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Submitted {shortDate(c.created_at)}
                {c.verified_owner && <span> · owner @{c.verified_owner}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Views</div>
              <div className="font-display tabular-nums">{fmtN(c.current_views)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Earned</div>
              <div className="font-display tabular-nums text-[hsl(43_92%_56%)]">{fmt$(c.earned_usd)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WithdrawalsList({ withdrawals }) {
  if (withdrawals.length === 0) {
    return <div className="text-sm text-muted-foreground text-center p-6 border border-white/10 rounded-md bg-white/[0.02]">No withdrawal requests</div>;
  }
  return (
    <div className="grid gap-2" data-testid="profile-withdrawals-list">
      {withdrawals.map((w) => {
        const c = {
          pending: 'border-white/10 text-muted-foreground',
          approved: 'border-[rgba(108,176,255,0.55)] text-[hsl(212_80%_75%)]',
          paid: 'border-[rgba(70,200,120,0.55)] text-[hsl(140_70%_70%)]',
          rejected: 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]',
        }[w.status] || 'border-white/10';
        return (
          <div key={w.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display text-base tabular-nums">{fmt$(w.amount_usd)}</div>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.22em] ${c}`}>{w.status}</Badge>
                <div className="text-[10px] text-muted-foreground">{shortDate(w.requested_at)}</div>
              </div>
              <div className="text-[10px] font-mono text-muted-foreground break-all mt-1">{w.usdt_bep20_address}</div>
              {(w.txhash || w.admin_notes) && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  {w.txhash && <div className="font-mono break-all">tx: {w.txhash}</div>}
                  {w.admin_notes && <div>note: {w.admin_notes}</div>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AuditList({ adjustments }) {
  if (adjustments.length === 0) {
    return <div className="text-sm text-muted-foreground text-center p-6 border border-white/10 rounded-md bg-white/[0.02]">No adjustments logged</div>;
  }
  return (
    <div className="grid gap-2" data-testid="profile-audit-list">
      {adjustments.map((a) => {
        const isCredit = (a.delta_earned_usd || a.delta_pending_usd || a.delta_held_usd || 0) > 0;
        const totalDelta = a.delta_pending_usd + a.delta_held_usd;
        return (
          <div key={a.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={
                'text-[10px] uppercase tracking-[0.22em] ' +
                (a.type === 'manual' ? 'border-[rgba(108,176,255,0.55)] text-[hsl(212_80%_75%)]' : 'border-white/10 text-muted-foreground')
              }>{a.type === 'manual' ? 'MANUAL' : 'AUTO ON CLIP'}</Badge>
              <div className={'font-display tabular-nums ' + (isCredit ? 'text-[hsl(140_65%_70%)]' : 'text-[hsl(0_72%_70%)]')}>
                {isCredit ? '+' : ''}{fmt$(totalDelta)}
              </div>
              {a.prev_status && a.new_status && (
                <div className="text-[10px] text-muted-foreground">
                  {a.prev_status} → {a.new_status}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground ml-auto">{shortDate(a.created_at)}</div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 grid gap-0.5">
              {a.note && <div>{a.note}</div>}
              <div className="text-[10px]">
                pending {a.delta_pending_usd >= 0 ? '+' : ''}{fmt$(a.delta_pending_usd)} · held {a.delta_held_usd >= 0 ? '+' : ''}{fmt$(a.delta_held_usd)}
                {a.delta_views ? ` · views ${a.delta_views > 0 ? '+' : ''}${fmtN(a.delta_views)}` : ''}
              </div>
              {a.admin_email && <div className="text-[10px]">by {a.admin_email}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
